/**
 * useOutreachSettings — the campaign's send configuration (zone,
 * sequence pick) and its pause switch.
 *
 * `exists: false` on the GET is not an error: the BE answers defaults for a
 * campaign that has never saved, so the form renders straight from it.
 * Save/pause/resume all answer the full fresh view, so each write seeds the
 * cache directly instead of forcing a refetch.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OutreachSequenceTemplateCheck, OutreachSettings, OutreachSettingsInput } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import {
  fetchOutreachSettings,
  pauseOutreach,
  putOutreachSequenceTemplate,
  putOutreachSettings,
  resumeOutreach,
} from '../api/outreachApi';

export function useOutreachSettings(campaignId: string) {
  return useQuery<OutreachSettings>({
    queryKey: campaignKeys.outreachSettings(campaignId),
    queryFn: () => fetchOutreachSettings(campaignId),
    enabled: !!campaignId,
    staleTime: 30_000,
  });
}

export function useOutreachSettingsMutations(campaignId: string) {
  const queryClient = useQueryClient();
  const seed = (settings: OutreachSettings) => {
    queryClient.setQueryData(campaignKeys.outreachSettings(campaignId), settings);
  };

  const save = useMutation<OutreachSettings, Error, OutreachSettingsInput>({
    mutationFn: (input) => putOutreachSettings(campaignId, input),
    onSuccess: seed,
  });

  const pause = useMutation<OutreachSettings, Error, void>({
    mutationFn: () => pauseOutreach(campaignId),
    onSuccess: seed,
  });

  const resume = useMutation<OutreachSettings, Error, void>({
    mutationFn: () => resumeOutreach(campaignId),
    onSuccess: seed,
  });

  // Task 6 / P8-6: the picker's own dry-check-then-commit, not the form's
  // Save.
  //
  // Go's PUT /settings is a full-replace, so a commit (`dry: false`) must
  // carry the campaign's current settings, not just the two dry-check
  // fields — see outreachApi.putOutreachSequenceTemplate. The caller
  // (SettingsView) supplies `currentSettings` only on the commit call; the
  // dry check keeps sending its minimal body.
  //
  // Round 3 / 412 regression: a commit's answer carries the full, freshly
  // written settings (including the `updatedAt` the write actually
  // bumped) — reseed the cache with the whole object via `seed()`, the
  // same way save/pause/resume do. Patching only `sequenceTemplateId`
  // would leave the cached `updatedAt` stale and false-412 the very next
  // write in the same session. The dry check's answer never carries
  // `settings` (nothing was written), so it falls through untouched.
  const changeSequenceTemplate = useMutation<
    OutreachSequenceTemplateCheck,
    Error,
    { sequenceTemplateId: number | null; dry: boolean; currentSettings?: OutreachSettingsInput }
  >({
    mutationFn: ({ sequenceTemplateId, dry, currentSettings }) =>
      currentSettings
        ? putOutreachSequenceTemplate(campaignId, sequenceTemplateId, dry, currentSettings)
        : putOutreachSequenceTemplate(campaignId, sequenceTemplateId, dry),
    onSuccess: (result, variables) => {
      if (variables.dry || !result.settings) return;
      seed(result.settings);
    },
  });

  return { save, pause, resume, changeSequenceTemplate };
}
